import type { NodeWithPos } from '@tiptap/core';
import type { Node } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { Node_Type } from 'ricos-schema';

import type { ChangeListChildrenIndent } from './commons';
import {
  changeIndentationOfEveryListSeparately,
  changeIndentationOfNotSelectedNodes,
  findParentList,
  getIndentationsInSelection,
  getListItemsInSelection,
  getNotSelectedDescendantListItems,
} from './commons';
import { sinkListItemNodes } from './indentList';
import { lifListItemNodes } from './lift-list-item-nodes';

export const outdentList =
  () =>
  ({ tr }) => {
    const { smallestIndentation } = getIndentationsInSelection(tr);
    if (smallestIndentation === 0) {
      return false;
    }
    return liftListItem(tr);
  };

export const liftListItem = (tr: Transaction) => {
  const parentList = findParentList(tr.selection.$from);
  if (!parentList) {
    return false;
  }

  const multipleListsInSelection = tr.selection.to > parentList.pos + parentList.node.nodeSize;

  if (multipleListsInSelection) {
    changeIndentationOfEveryListSeparately(tr, liftChildrenOfList);
    return true;
  }

  liftChildrenOfList(tr, parentList);
  return true;
};

const liftChildrenOfList: ChangeListChildrenIndent = (tr, parentList) => {
  const selectedListItems = getListItemsInSelection(tr, parentList);
  const areListItemsLiftedSemantically = lifListItemNodes(tr, selectedListItems, parentList);

  if (!areListItemsLiftedSemantically) {
    // this affects the list parent so other sibling list items that are in the same parent but are not in selection need to be sunk
    const notSelectedSiblingListItems = getNotSelectedSiblingListItems(tr, parentList);
    changeIndentationOfNotSelectedNodes(tr, notSelectedSiblingListItems, sinkListItemNodes);
  }
  // we always need to sink children list items that are not in selection of lifted list items
  const notSelectedDescendantListItems = getNotSelectedDescendantListItems(tr, selectedListItems);
  changeIndentationOfNotSelectedNodes(tr, notSelectedDescendantListItems, sinkListItemNodes);

  return selectedListItems;
};

const getNotSelectedSiblingListItems = (tr: Transaction, parentList: NodeWithPos) => {
  const notSelectedSiblingListItems: NodeWithPos[] = [];

  parentList.node.descendants((childNode: Node, childPos: number) => {
    const childPosInDoc = childPos + parentList.pos + 1;
    if (childNode.type.name !== Node_Type.LIST_ITEM || childPosInDoc < tr.selection.to) {
      return false;
    }
    notSelectedSiblingListItems.push({ node: childNode, pos: childPosInDoc });
  });

  return notSelectedSiblingListItems;
};
